/**
 * CardDrawRenderer — 3-card selection overlay for roguelite checkpoints.
 *
 * Follows the PostMatch/Lottery UI patterns:
 * - PixiJS Container on uiContainer, high zIndex
 * - Staggered entrance animation
 * - Cursor highlight with glow pulse
 * - State-driven: automatically shows/hides based on GameState phase
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, ROGUELITE, LOTTERY } from '../../constants';
import type { GameState } from '../../types';
import type { CardOption } from '../../roguelite/card-draw';

const CARD = {
  WIDTH: 280,
  HEIGHT: 360,
  GAP: 24,
  Y: 200,
  CORNER_RADIUS: 12,
  TITLE_SIZE: 20,
  DESC_SIZE: 14,
  RARITY_SIZE: 12,
  BORDER_WIDTH: 2.5,
  GLOW_PULSE_SPEED: 4,
  ENTRANCE_DURATION: 0.4,
  BG_ALPHA: 0.85,
} as const;

const RARITY_COLORS: Record<string, number> = {
  common: LOTTERY.COLOR_COMMON,
  uncommon: LOTTERY.COLOR_UNCOMMON,
  rare: LOTTERY.COLOR_RARE,
  epic: LOTTERY.COLOR_EPIC,
  legendary: LOTTERY.COLOR_LEGENDARY,
};

function monoStyle(size: number, color: number, bold = false): TextStyle {
  return new TextStyle({
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: size,
    fill: color,
    fontWeight: bold ? 'bold' : 'normal',
    wordWrap: true,
    wordWrapWidth: CARD.WIDTH - 40,
  });
}

export class CardDrawRenderer {
  private container: Container;
  private overlay: Graphics;
  private cards: Container[] = [];
  private cardBgs: Graphics[] = [];
  private cardTitles: Text[] = [];
  private cardDescs: Text[] = [];
  private cardRarities: Text[] = [];
  private headerText: Text;
  private showing = false;
  private elapsed = 0;
  private entranceTimer = 0;
  private cursor = 0;
  // Track whether we already populated for the current draw
  private populatedForOptions: CardOption[] | null = null;

  constructor(uiContainer: Container) {
    this.container = new Container();
    this.container.zIndex = 250;
    this.container.visible = false;
    uiContainer.addChild(this.container);

    // Semi-transparent background overlay
    this.overlay = new Graphics();
    this.container.addChild(this.overlay);

    // Header text
    this.headerText = new Text({
      text: 'CHOOSE A CARD',
      style: monoStyle(28, 0xffffff, true),
    });
    this.headerText.anchor.set(0.5, 0);
    this.headerText.position.set(WORLD.CANVAS_WIDTH / 2, CARD.Y - 60);
    this.container.addChild(this.headerText);

    // Create 3 card panels
    const totalWidth = ROGUELITE.CARD_COUNT * CARD.WIDTH + (ROGUELITE.CARD_COUNT - 1) * CARD.GAP;
    const startX = (WORLD.CANVAS_WIDTH - totalWidth) / 2;

    for (let i = 0; i < ROGUELITE.CARD_COUNT; i++) {
      const cardContainer = new Container();
      cardContainer.position.set(startX + i * (CARD.WIDTH + CARD.GAP), CARD.Y);
      this.container.addChild(cardContainer);

      const bg = new Graphics();
      cardContainer.addChild(bg);

      const title = new Text({ text: '', style: monoStyle(CARD.TITLE_SIZE, 0xffffff, true) });
      title.anchor.set(0.5, 0);
      title.position.set(CARD.WIDTH / 2, 30);
      cardContainer.addChild(title);

      const rarity = new Text({ text: '', style: monoStyle(CARD.RARITY_SIZE, 0x888888) });
      rarity.anchor.set(0.5, 0);
      rarity.position.set(CARD.WIDTH / 2, 60);
      cardContainer.addChild(rarity);

      const desc = new Text({ text: '', style: monoStyle(CARD.DESC_SIZE, 0xaaaaaa) });
      desc.anchor.set(0.5, 0);
      desc.position.set(CARD.WIDTH / 2, 100);
      cardContainer.addChild(desc);

      this.cards.push(cardContainer);
      this.cardBgs.push(bg);
      this.cardTitles.push(title);
      this.cardDescs.push(desc);
      this.cardRarities.push(rarity);
    }
  }

  private populateCards(options: CardOption[], drawType: string | null): void {
    this.populatedForOptions = options;
    this.showing = true;
    this.container.visible = true;
    this.entranceTimer = CARD.ENTRANCE_DURATION;
    this.cursor = 0;
    this.elapsed = 0;

    // Update header
    if (drawType === 'weapon1') {
      this.headerText.text = 'CHOOSE YOUR PRIMARY WEAPON';
    } else if (drawType === 'weapon2') {
      this.headerText.text = 'CHOOSE YOUR SECONDARY WEAPON';
    } else {
      this.headerText.text = 'CHOOSE A MODIFIER';
    }

    // Render overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
    this.overlay.fill({ color: 0x000000, alpha: 0.7 });

    // Populate cards
    for (let i = 0; i < ROGUELITE.CARD_COUNT; i++) {
      const option = options[i];
      if (option) {
        this.cardTitles[i].text = option.name.toUpperCase();
        this.cardDescs[i].text = option.description;
        const rarityColor = RARITY_COLORS[option.rarity] ?? 0xffffff;
        this.cardRarities[i].text = option.rarity.toUpperCase();
        this.cardRarities[i].style.fill = rarityColor;
        this.cardTitles[i].style.fill = rarityColor;
        this.cards[i].visible = true;
      } else {
        this.cards[i].visible = false;
      }
    }
  }

  hide(): void {
    this.showing = false;
    this.container.visible = false;
    this.populatedForOptions = null;
  }

  setCursor(index: number): void {
    this.cursor = index;
  }

  update(state: GameState, dt: number): void {
    const ms = state.modeState;

    // Auto-show/hide based on phase
    if (state.phase === 'roguelite_card_draw' && ms.rogueliteCardDrawOptions) {
      if (ms.rogueliteCardDrawOptions !== this.populatedForOptions) {
        this.populateCards(ms.rogueliteCardDrawOptions, ms.rogueliteCardDrawType);
      }
      this.cursor = ms.rogueliteCardDrawCursor;
    } else if (this.showing) {
      this.hide();
      return;
    }

    if (!this.showing) return;
    this.elapsed += dt;

    // Entrance animation
    if (this.entranceTimer > 0) {
      this.entranceTimer = Math.max(0, this.entranceTimer - dt);
    }

    const progress = 1 - this.entranceTimer / CARD.ENTRANCE_DURATION;
    const totalWidth = ROGUELITE.CARD_COUNT * CARD.WIDTH + (ROGUELITE.CARD_COUNT - 1) * CARD.GAP;
    const startX = (WORLD.CANVAS_WIDTH - totalWidth) / 2;

    // Update each card
    for (let i = 0; i < ROGUELITE.CARD_COUNT; i++) {
      const card = this.cards[i];
      if (!card.visible) continue;

      const isSelected = i === this.cursor;
      const bg = this.cardBgs[i];
      const rarityText = this.cardRarities[i];
      const rarityColor = (rarityText.style.fill as number) || 0xffffff;

      // Staggered entrance: each card enters with slight delay
      const cardDelay = i * 0.08;
      const cardProgress = Math.max(0, Math.min(1, (progress - cardDelay) / 0.7));
      const easeOut = 1 - Math.pow(1 - cardProgress, 3);

      // Scale and offset for entrance
      card.scale.set(0.8 + 0.2 * easeOut);
      card.alpha = easeOut;

      // Selected card offset
      const selectedOffset = isSelected ? -10 : 0;
      card.position.x = startX + i * (CARD.WIDTH + CARD.GAP);
      card.position.y = CARD.Y + selectedOffset;

      // Draw card background
      bg.clear();
      bg.roundRect(0, 0, CARD.WIDTH, CARD.HEIGHT, CARD.CORNER_RADIUS);
      bg.fill({ color: 0x0a0a20, alpha: CARD.BG_ALPHA });

      // Border: selected = bright pulse, unselected = dim
      if (isSelected) {
        const pulse = 0.7 + Math.sin(this.elapsed * CARD.GLOW_PULSE_SPEED) * 0.3;
        bg.roundRect(0, 0, CARD.WIDTH, CARD.HEIGHT, CARD.CORNER_RADIUS);
        bg.stroke({ color: rarityColor, width: CARD.BORDER_WIDTH + 1, alpha: pulse });
      } else {
        bg.roundRect(0, 0, CARD.WIDTH, CARD.HEIGHT, CARD.CORNER_RADIUS);
        bg.stroke({ color: rarityColor, width: CARD.BORDER_WIDTH, alpha: 0.3 });
      }
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
